import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  FaEye, FaFire, FaHardHat, FaUsers, FaCogs, FaBolt,
  FaBuilding, FaFirstAid, FaLock, FaTint, FaUserTie,
  FaTimes, FaChevronRight, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaMapMarkerAlt,
  FaShieldAlt, FaFileAlt, FaClock,
  FaUserCheck, FaBriefcase, FaCube
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';

/* ── Icon + colour map for each safety area ──────────────────── */
const AREA_META = {
  fire:       { icon: FaFire,     color: 'red' },
  roof:       { icon: FaHardHat,  color: 'blue' },
  staff:      { icon: FaUsers,    color: 'purple' },
  equipment:  { icon: FaCogs,     color: 'amber' },
  electrical: { icon: FaBolt,     color: 'amber' },
  building:   { icon: FaBuilding, color: 'teal' },
  emergency:  { icon: FaFirstAid, color: 'red' },
  security:   { icon: FaLock,     color: 'gray' },
  health:     { icon: FaTint,     color: 'blue' },
};

/* ── Duration helper ─────────────────────────────────────────── */
function formatDuration(startTime) {
  const now = new Date();
  const start = new Date(startTime);
  const diffMs = Math.max(0, now - start);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just arrived';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins === 0 ? `${hrs}h` : `${hrs}h ${remMins}m`;
}

export default function PropertyDashboardOverview() {
  const ctx = useOutletContext();
  const {
    property, id, expiringDocs, expiryBannerDismissed, setExpiryBannerDismissed,
    filteredSafetyCategories, handleNavigation,
    allowedSections, hasRestrictions,
    showInspectionModal, setShowInspectionModal, availableEquipment,
    inspectionForm, handleInspectionFormChange, handleInspectionSubmit
  } = ctx;

  const [currentOccupants, setCurrentOccupants] = useState([]);
  const [occupancyLoading, setOccupancyLoading] = useState(true);
  const [testSummary, setTestSummary] = useState({ total: 0, passed: 0, failed: 0, pending: 0, overdue: 0, expiringSoon: 0 });

  const fetchOccupancy = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        getApiEndpoint(`/api/users/checkins/${id}/occupancy`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCurrentOccupants(data.current || []);
    } catch {} finally { setOccupancyLoading(false); }
  }, [id]);

  const fetchTestSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiEndpoint(`/api/dashboard/test-statistics?propertyId=${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const c = data.counters || {};
        setTestSummary({
          total: c.total || 0, passed: c.passed || 0, failed: c.failed || 0,
          pending: c.pending || 0, overdue: c.overdue || 0, expiringSoon: c.expiringSoon || 0
        });
      }
    } catch {}
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchOccupancy();
    fetchTestSummary();
    const poll = setInterval(fetchOccupancy, 60000);
    return () => clearInterval(poll);
  }, [id, fetchOccupancy, fetchTestSummary]);

  const totalAssets = filteredSafetyCategories.reduce((s, c) => s + (c.assets || 0), 0);
  // Open issues = failed/overdue PLUS untested/unknown — all need investigation.
  const totalIssues = filteredSafetyCategories.reduce(
    (s, c) => s + (c.issues || 0) + (c.untested || 0), 0
  );
  // Compliance rate = passed / total-assets. Unknown/untested assets stay in the
  // denominator so they drag the rate down — don't silently treat them as compliant.
  const complianceRate = testSummary.total > 0
    ? Math.round((testSummary.passed / testSummary.total) * 100)
    : 0;

  const barWidth = complianceRate;
  const barColor = complianceRate >= 80 ? '#1A80E5' : complianceRate >= 50 ? '#d97706' : '#ef4444';

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return imageUrl.startsWith('uploads/')
      ? getApiEndpoint(`/api/${imageUrl}`)
      : getApiEndpoint(`/api/uploads/${imageUrl}`);
  };

  const thumbUrl = getImageUrl(property.image_url);

  return (
    <div className="pdo">
      {/* Document expiry banner */}
      {expiringDocs.length > 0 && !expiryBannerDismissed && (
        <div className="pdo-alert-banner" role="alert">
          <FaExclamationTriangle className="pdo-alert-icon" />
          <span className="pdo-alert-text">
            <strong>{expiringDocs.length} document{expiringDocs.length > 1 ? 's' : ''}</strong> expiring within 30 days
            {expiringDocs.length <= 3 && <> — {expiringDocs.map(d => d.name || d.document_name).join(', ')}</>}
          </span>
          <button className="pdo-alert-action" onClick={() => handleNavigation('documents')}>View Documents</button>
          <button className="pdo-alert-dismiss" onClick={() => setExpiryBannerDismissed(true)} aria-label="Dismiss">
            <FaTimes />
          </button>
        </div>
      )}

      {/* ─── Compact Property Header ─── */}
      <div className="pdo-header-bar">
        <div className="pdo-header-thumb" style={{ backgroundImage: thumbUrl ? `url(${thumbUrl})` : 'none' }}>
          {!thumbUrl && <FaBuilding />}
        </div>
        <div className="pdo-header-info">
          <h1 className="pdo-header-name">
            <span className="pdo-header-name-text">{property.name || 'Loading...'}</span>
            {property.property_code && (
              <span className="pdo-header-code">#{property.property_code}</span>
            )}
          </h1>
          {property.address && (
            <p className="pdo-header-addr"><FaMapMarkerAlt /> {property.address}</p>
          )}
        </div>
        <div className="pdo-header-actions">
          {(!hasRestrictions || (allowedSections || []).includes('management')) && (
            <button className="pdo-hdr-btn" onClick={() => handleNavigation('management')}>
              <FaUserTie /> Management
            </button>
          )}
          <button className="pdo-hdr-btn pdo-hdr-btn-outline" onClick={() => handleNavigation('drawings')}>
            <FaMapMarkerAlt /> Drawings
          </button>
          <button className="pdo-hdr-btn pdo-hdr-btn-outline" onClick={() => handleNavigation('tours')}>
            <FaCube /> 3D Tours
          </button>
          <button className="pdo-hdr-btn pdo-hdr-btn-outline pdo-hdr-btn-hide-mobile" onClick={() => handleNavigation('documents')}>
            <FaFileAlt /> Documents
          </button>
        </div>
      </div>

      {/* ─── Stat Bar (inline, compact) ─── */}
      <div className="pdo-stat-bar">
        <div className="pdo-stat">
          <span className="pdo-stat-val">{totalAssets}</span>
          <span className="pdo-stat-lbl">Assets</span>
        </div>
        <div className="pdo-stat-divider" />
        <div className="pdo-stat pdo-stat-pass">
          <FaCheckCircle className="pdo-stat-ico" />
          <span className="pdo-stat-val">{testSummary.passed || 0}</span>
          <span className="pdo-stat-lbl">Passed</span>
        </div>
        <div className="pdo-stat-divider" />
        <div className="pdo-stat pdo-stat-fail">
          <FaTimesCircle className="pdo-stat-ico" />
          <span className="pdo-stat-val">{testSummary.failed || 0}</span>
          <span className="pdo-stat-lbl">Failed</span>
        </div>
        <div className="pdo-stat-divider" />
        <div className="pdo-stat pdo-stat-overdue">
          <FaClock className="pdo-stat-ico" />
          <span className="pdo-stat-val">{testSummary.overdue || 0}</span>
          <span className="pdo-stat-lbl">Overdue</span>
        </div>
        <div className="pdo-stat-divider" />
        <div className="pdo-stat pdo-stat-onsite">
          <FaUserCheck className="pdo-stat-ico" />
          <span className="pdo-stat-val">{currentOccupants.length}</span>
          <span className="pdo-stat-lbl">On Site</span>
        </div>

        {/* Compliance bar inline */}
        <div className="pdo-stat-compliance">
          <div className="pdo-stat-compliance-label">
            <span className="pdo-stat-compliance-pct" style={{ color: barColor }}>{complianceRate}%</span>
            <span className="pdo-stat-compliance-txt">Compliance</span>
          </div>
          <div className="pdo-stat-compliance-track">
            <div className="pdo-stat-compliance-fill" style={{ width: `${barWidth}%`, background: barColor }} />
          </div>
        </div>
      </div>

      {/* ─── Two-Column Layout ─── */}
      <div className="pdo-layout">
        {/* Left: Safety Modules (primary focus) */}
        <div className="pdo-main-col">
          <div className="pdo-card pdo-modules-card">
            <div className="pdo-card-top">
              <h2 className="pdo-card-title">
                <FaShieldAlt className="pdo-card-title-ico" /> Safety Areas
              </h2>
              <span className="pdo-card-count">{filteredSafetyCategories.length} modules</span>
            </div>
            <div className="pdo-modules-grid">
              {filteredSafetyCategories.map((cat) => {
                const meta = AREA_META[cat.id] || { icon: FaEye, color: 'blue' };
                const Icon = meta.icon;
                const untested = cat.untested || 0;
                // Red pulse: explicit issues (failed/overdue), or a large untested backlog (≥10).
                // Orange pulse: a handful of untested items to investigate.
                let pulseClass = '';
                if (cat.issues > 0 || untested >= 10) pulseClass = 'pdo-mod-pulse-red';
                else if (untested > 0) pulseClass = 'pdo-mod-pulse-orange';
                return (
                  <button
                    key={cat.id}
                    className={`pdo-mod-tile ${pulseClass}`}
                    onClick={() => handleNavigation(cat.id)}
                  >
                    <div className={`pdo-mod-ico pdo-mi-${meta.color}`}>
                      <Icon />
                    </div>
                    <div className="pdo-mod-body">
                      <span className="pdo-mod-name">{cat.name}</span>
                      <span className="pdo-mod-count">
                        {cat.assets} {cat.assets === 1 ? 'asset' : 'assets'}
                        {untested > 0 && <span className="pdo-mod-untested"> · {untested} untested — investigate</span>}
                      </span>
                    </div>
                    {cat.issues > 0 && <span className="pdo-mod-issues">{cat.issues}</span>}
                    <FaChevronRight className="pdo-mod-arrow" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Compliance Breakdown */}
          <div className="pdo-card pdo-breakdown-card">
            <div className="pdo-card-top">
              <h2 className="pdo-card-title">
                <FaShieldAlt className="pdo-card-title-ico" /> Test Status Breakdown
              </h2>
            </div>
            <div className="pdo-breakdown-bars">
              {[
                { label: 'Passed', val: testSummary.passed || 0, total: testSummary.total || totalAssets, color: '#1A80E5' },
                { label: 'Failed', val: testSummary.failed || 0, total: testSummary.total || totalAssets, color: '#ef4444' },
                { label: 'Pending', val: testSummary.pending || 0, total: testSummary.total || totalAssets, color: '#d97706' },
              ].map(b => (
                <div key={b.label} className="pdo-bar-row">
                  <span className="pdo-bar-label">{b.label}</span>
                  <div className="pdo-bar-track">
                    <div className="pdo-bar-fill" style={{ width: b.total > 0 ? `${(b.val / b.total) * 100}%` : '0%', background: b.color }} />
                  </div>
                  <span className="pdo-bar-val">{b.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: On Site + Quick Info */}
        <div className="pdo-side-col">
          {/* On Site Now */}
          <div className="pdo-card pdo-onsite-card">
            <div className="pdo-card-top">
              <h2 className="pdo-card-title">
                <FaUserCheck className="pdo-card-title-ico pdo-ico-teal" /> On Site
              </h2>
              <button className="pdo-card-link" onClick={() => handleNavigation('logs')}>
                View All <FaChevronRight />
              </button>
            </div>
            {occupancyLoading ? (
              <div className="pdo-empty"><p>Loading...</p></div>
            ) : currentOccupants.length === 0 ? (
              <div className="pdo-empty">
                <FaUsers className="pdo-empty-ico" />
                <p>No one currently on site</p>
              </div>
            ) : (
              <div className="pdo-people">
                {currentOccupants.slice(0, 6).map(person => (
                  <div key={person.id} className="pdo-person">
                    <div className="pdo-person-av">
                      {(person.person_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="pdo-person-detail">
                      <span className="pdo-person-nm">{person.person_name}</span>
                      <span className="pdo-person-co">
                        {person.company && <><FaBriefcase className="pdo-sm-ico" /> {person.company}</>}
                        {person.person_role && !person.company && person.person_role}
                      </span>
                    </div>
                    <span className="pdo-person-dur">
                      <FaClock className="pdo-sm-ico" /> {formatDuration(person.check_in_time)}
                    </span>
                  </div>
                ))}
                {currentOccupants.length > 6 && (
                  <div className="pdo-more">+{currentOccupants.length - 6} more on site</div>
                )}
              </div>
            )}
          </div>

          {/* Property Quick Info */}
          <div className="pdo-card pdo-info-card">
            <div className="pdo-card-top">
              <h2 className="pdo-card-title">
                <FaBuilding className="pdo-card-title-ico" /> Property Info
              </h2>
            </div>
            <div className="pdo-info-rows">
              <div className="pdo-info-row">
                <span className="pdo-info-key">Type</span>
                <span className="pdo-info-val">{property.type || 'Commercial'}</span>
              </div>
              {property.floors && (
                <div className="pdo-info-row">
                  <span className="pdo-info-key">Floors</span>
                  <span className="pdo-info-val">{property.floors}</span>
                </div>
              )}
              {property.postcode && (
                <div className="pdo-info-row">
                  <span className="pdo-info-key">Postcode</span>
                  <span className="pdo-info-val">{property.postcode}</span>
                </div>
              )}
              <div className="pdo-info-row">
                <span className="pdo-info-key">Total Assets</span>
                <span className="pdo-info-val">{totalAssets}</span>
              </div>
              <div className="pdo-info-row">
                <span className="pdo-info-key">Open Issues</span>
                <span className="pdo-info-val pdo-info-val-warn">{totalIssues}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Inspection Modal ─── */}
      {showInspectionModal && (
        <div className="pd-modal-overlay" onClick={() => setShowInspectionModal(false)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <h2>Start New Inspection</h2>
              <button className="pd-modal-close" onClick={() => setShowInspectionModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleInspectionSubmit} className="pd-modal-body">
              <div className="pd-form-group">
                <label htmlFor="inspectionType">Inspection Type</label>
                <select id="inspectionType" value={inspectionForm.inspectionType}
                  onChange={(e) => handleInspectionFormChange('inspectionType', e.target.value)} required>
                  <option value="Routine">Routine</option>
                  <option value="Post-Installation">Post-Installation</option>
                  <option value="Post-Incident">Post-Incident</option>
                  <option value="Recall">Recall</option>
                </select>
              </div>
              <div className="pd-form-group">
                <label htmlFor="equipmentType">Equipment Type</label>
                <select id="equipmentType" value={inspectionForm.equipmentType}
                  onChange={(e) => { handleInspectionFormChange('equipmentType', e.target.value); handleInspectionFormChange('equipmentId', ''); }} required>
                  <option value="">Select Equipment Type</option>
                  <option value="anchor_point">Anchor Point</option>
                  <option value="cradle">Cradle</option>
                  <option value="abseil_system">Abseil System</option>
                  <option value="ladder">Ladder</option>
                  <option value="handrail">Handrail</option>
                  <option value="static_line">Static Line</option>
                  <option value="walkway">Walkway</option>
                  <option value="fixed_equipment">Fixed Equipment</option>
                </select>
              </div>
              {inspectionForm.equipmentType && (
                <div className="pd-form-group">
                  <label htmlFor="equipmentId">Select Equipment</label>
                  <select id="equipmentId" value={inspectionForm.equipmentId}
                    onChange={(e) => handleInspectionFormChange('equipmentId', e.target.value)} required>
                    <option value="">Select Equipment</option>
                    {availableEquipment
                      .filter(eq => eq.type === inspectionForm.equipmentType)
                      .map(eq => <option key={eq.id} value={eq.id}>{eq.name} - {eq.location_description}</option>)}
                  </select>
                </div>
              )}
              <div className="pd-form-group">
                <label htmlFor="notes">Notes (Optional)</label>
                <textarea id="notes" value={inspectionForm.notes}
                  onChange={(e) => handleInspectionFormChange('notes', e.target.value)}
                  placeholder="Add any additional notes..." rows="3" />
              </div>
              <div className="pd-modal-actions">
                <button type="button" className="pd-btn-secondary" onClick={() => setShowInspectionModal(false)}>Cancel</button>
                <button type="submit" className="pd-btn-primary">Start Inspection</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
